import React from 'react';
import { useSelector } from 'react-redux';
import { selectWorkersLoading } from 'store/slices/workersSlice';
import { useWizard, WizardStepLayout } from 'components/layout/wizards';
import {
  WORKER_TYPES,
  WORKER_ACTION_TYPES,
  WORKER_ACTION_TYPES_OPTIONS,
  WORKER_PARSER_METHODS,
  WORKER_PATH_OPERATIONS,
  WORKER_PATH_OPERATIONS_LABELS
} from 'constants/workers';

export default function WorkerReviewStep() {
  const { nextStep, data } = useWizard();
  const loading = useSelector(selectWorkersLoading);

  const handleCreate = () => {
    nextStep(data);
  };

  const getActionLabel = (actionValue) => {
    const option = WORKER_ACTION_TYPES_OPTIONS.find(opt => opt.value === actionValue);
    return option ? option.label : actionValue;
  };

  return (
    <WizardStepLayout
      title="Review Worker"
      description="Review the details before creating the worker."
      primaryAction={{
        label: loading ? 'Creating Worker...' : 'Create Worker',
        onClick: handleCreate,
        disabled: loading
      }}
    >
      <div className="space-y-6 text-sm text-gray-300">
        <div className="rounded-md bg-gray-800 p-4">
          <h3 className="font-medium text-white mb-2">General</h3>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
            <div>
              <dt className="text-gray-500">Name</dt>
              <dd className="text-white">{data.name}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Type</dt>
              <dd className="text-white">{data.type === WORKER_TYPES.PARSER ? 'Parser' : 'Prebuild'}</dd>
            </div>
          </dl>
        </div>

        {data.type === WORKER_TYPES.PARSER && (
          <>
            <div className="rounded-md bg-gray-800 p-4">
              <h3 className="font-medium text-white mb-2">Request Details</h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">URL</dt>
                  <dd className="text-white break-all">{data.url}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Method</dt>
                  <dd className="text-white">{data.method}</dd>
                </div>
                {data.method === WORKER_PARSER_METHODS.POST && (
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500">Body</dt>
                    <dd className="text-white font-mono whitespace-pre-wrap">
                      {typeof data.body === 'object' ? JSON.stringify(data.body, null, 2) : data.body}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="rounded-md bg-gray-800 p-4">
              <h3 className="font-medium text-white mb-2">Validation</h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <div>
                  <dt className="text-gray-500">Type</dt>
                  <dd className="text-white">{data.validationType}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Expression</dt>
                  <dd className="text-white font-mono">{data.expression}</dd>
                </div>
                {data.operation && data.operation !== WORKER_PATH_OPERATIONS.NONE && (
                  <>
                    <div>
                      <dt className="text-gray-500">Operator</dt>
                      <dd className="text-white">{WORKER_PATH_OPERATIONS_LABELS[data.operation] || data.operation}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Compare Value</dt>
                      <dd className="text-white font-mono">{data.controlValue}</dd>
                    </div>
                  </>
                )}
              </dl>
            </div>

            <div className="rounded-md bg-gray-800 p-4">
              <h3 className="font-medium text-white mb-2">Logic</h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <div>
                  <dt className="text-gray-500">On Success</dt>
                  <dd className="text-white">
                    {getActionLabel(data.onSuccess)}
                    {data.onSuccess === WORKER_ACTION_TYPES.UPDATE_MESSAGE && data.onSuccessMessage && (
                      <div className="mt-1 text-xs text-gray-400 italic">
                        "{data.onSuccessMessage}"
                      </div>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">On Failure</dt>
                  <dd className="text-white">
                    {getActionLabel(data.onFailure)}
                    {data.onFailure === WORKER_ACTION_TYPES.UPDATE_MESSAGE && data.onFailureMessage && (
                      <div className="mt-1 text-xs text-gray-400 italic">
                        "{data.onFailureMessage}"
                      </div>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </>
        )}

        {data.type === WORKER_TYPES.PREBUILD && (
          <div className="rounded-md bg-gray-800 p-4">
            <h3 className="font-medium text-white mb-2">Selected Worker</h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="text-gray-500">Name</dt>
                <dd className="text-white">{data.selectedWorker?.name}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-gray-500">Description</dt>
                <dd className="text-white">{data.selectedWorker?.description}</dd>
              </div>
            </dl>
          </div>
        )}
      </div>
    </WizardStepLayout>
  );
}
